import React, { useEffect, useMemo, useState } from 'react';
import QRCode from 'qrcode';
import { toast } from 'sonner';
import { Copy, MessageCircle, MessageSquare, QrCode, X, Share2 } from 'lucide-react';

/**
 * ShareModal — one-click share of a LIVE broadcast + slot.
 *
 * Props
 *   open                bool
 *   onClose             fn
 *   broadcastId         string   (required)
 *   slotNumber          number|null (currently-announced slot; drives ?slot= param)
 *   sellerName          string
 *   productName         string
 *   price               number
 *
 * The share URL is `${origin}/live/{broadcastId}?slot={slotNumber}` — routing
 * this back to `LiveBroadcastView` which already auto-opens the OptionSheet
 * for the deep-linked slot (existing Phase-3 behaviour).
 *
 * Kakao SDK: loaded on-demand from //developers.kakao.com/sdk/js/kakao.min.js.
 * If it fails (network / no key), we transparently fall back to copy-to-clipboard.
 */
const ShareModal = ({ open, onClose, broadcastId, slotNumber, sellerName, productName, price }) => {
  const [tab, setTab] = useState('menu'); // menu | qr
  const [qrDataUrl, setQrDataUrl] = useState('');
  const shareUrl = useMemo(() => {
    if (!broadcastId) return '';
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return `${origin}/live/${broadcastId}${slotNumber ? `?slot=${slotNumber}` : ''}`;
  }, [broadcastId, slotNumber]);

  // Rebuild QR whenever the URL changes; keep it cheap (client-side).
  useEffect(() => {
    if (!shareUrl) { setQrDataUrl(''); return; }
    QRCode.toDataURL(shareUrl, { margin: 1, width: 220, errorCorrectionLevel: 'M' })
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(''));
  }, [shareUrl]);

  const message = useMemo(() => {
    const parts = ['🔴 LIVE 방송중'];
    if (sellerName) parts.push(sellerName);
    if (productName) {
      const numbered = slotNumber ? `${['①','②','③','④','⑤','⑥','⑦','⑧','⑨','⑩'][Math.max(0, slotNumber - 1)] || slotNumber+'번'} ${productName}` : productName;
      parts.push(`현재 소개 상품: ${numbered}`);
    }
    if (price) parts.push(`${Number(price).toLocaleString('ko-KR')}원`);
    parts.push('바로 구매하기 👉');
    parts.push(shareUrl);
    return parts.join('\n');
  }, [sellerName, productName, price, slotNumber, shareUrl]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('링크가 복사되었습니다.');
    } catch {
      // Fallback for older mobile browsers.
      const t = document.createElement('textarea');
      t.value = shareUrl; document.body.appendChild(t); t.select();
      try { document.execCommand('copy'); toast.success('링크가 복사되었습니다.'); } catch { toast.error('복사에 실패했습니다. 링크를 길게 눌러 복사해주세요.'); }
      finally { t.remove(); }
    }
  };

  const shareToKakao = async () => {
    try {
      // Load SDK on demand (only once).
      if (!window.Kakao) {
        await new Promise((resolve, reject) => {
          const s = document.createElement('script');
          s.src = 'https://t1.kakaocdn.net/kakao_js_sdk/2.7.2/kakao.min.js';
          s.onload = resolve; s.onerror = reject;
          document.head.appendChild(s);
        });
      }
      const key = window?.process?.env?.REACT_APP_KAKAO_JS_KEY || '';
      if (window.Kakao && !window.Kakao.isInitialized()) {
        if (!key) throw new Error('no-key');
        window.Kakao.init(key);
      }
      if (window.Kakao?.Share?.sendDefault) {
        window.Kakao.Share.sendDefault({
          objectType: 'feed',
          content: {
            title: `🔴 LIVE 방송중 — ${sellerName || ''}`.trim(),
            description: `${productName ? `현재 소개: ${productName}\n` : ''}${price ? `${Number(price).toLocaleString('ko-KR')}원\n` : ''}지금 바로 구매하세요!`,
            imageUrl: `${window.location.origin}/logo512.png`,
            link: { mobileWebUrl: shareUrl, webUrl: shareUrl },
          },
          buttons: [{ title: '바로 구매하기', link: { mobileWebUrl: shareUrl, webUrl: shareUrl } }],
        });
        toast.success('카카오톡으로 공유했습니다.');
      } else {
        throw new Error('no-sdk');
      }
    } catch (e) {
      // Graceful fallback: use Web Share API when available (native OS sheet),
      // else copy the link.
      if (navigator.share) {
        try { await navigator.share({ title: '라이브 방송', text: message, url: shareUrl }); return; } catch {}
      }
      await copy();
      toast.info('카카오톡을 사용할 수 없어 링크를 복사했습니다.');
    }
  };

  const shareSms = () => {
    // Format that works on both iOS and Android per RFC 5724.
    // We pre-fill body via a query param; the OS opens the messaging app.
    const url = `sms:?&body=${encodeURIComponent(message)}`;
    window.location.href = url;
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="share-modal">
      <div className="bg-white dark:bg-[#141414] rounded-t-2xl sm:rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <h3 className="text-base font-black inline-flex items-center gap-2"><Share2 size={16} className="text-[#FF3B30]" /> 공유</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-black/5 dark:hover:bg-white/10" data-testid="share-close"><X size={16} /></button>
        </div>

        <div className="px-5 pb-5 space-y-4">
          {/* Preview card */}
          <div className="rounded-2xl border border-black/5 dark:border-white/10 p-3 text-xs bg-black/[0.02] dark:bg-white/[0.03]" data-testid="share-preview">
            <p className="font-black text-[#FF3B30] mb-1">🔴 LIVE 방송중</p>
            {sellerName && <p className="font-bold">{sellerName}</p>}
            {productName && <p className="text-sm font-bold truncate">{slotNumber ? `${slotNumber}번 · ` : ''}{productName}</p>}
            {price ? <p className="text-sm font-black text-[#FF3B30]">{Number(price).toLocaleString('ko-KR')}원</p> : null}
            <p className="mt-2 text-[10px] text-gray-500 break-all">{shareUrl}</p>
          </div>

          {tab === 'menu' ? (
            <div className="grid grid-cols-4 gap-2" data-testid="share-menu">
              <button onClick={shareToKakao} className="flex flex-col items-center gap-1 py-3 rounded-xl bg-[#FEE500] text-[#181600] font-black text-[11px] hover:brightness-95 transition" data-testid="share-kakao">
                <MessageCircle size={22} />
                카카오톡
              </button>
              <button onClick={copy} className="flex flex-col items-center gap-1 py-3 rounded-xl bg-black text-white font-black text-[11px] hover:bg-[#FF3B30] transition" data-testid="share-copy">
                <Copy size={22} />
                링크 복사
              </button>
              <button onClick={shareSms} className="flex flex-col items-center gap-1 py-3 rounded-xl border border-black/10 dark:border-white/10 font-black text-[11px] hover:border-[#FF3B30] hover:text-[#FF3B30] transition" data-testid="share-sms">
                <MessageSquare size={22} />
                문자
              </button>
              <button onClick={() => setTab('qr')} className="flex flex-col items-center gap-1 py-3 rounded-xl border border-black/10 dark:border-white/10 font-black text-[11px] hover:border-[#FF3B30] hover:text-[#FF3B30] transition" data-testid="share-qr">
                <QrCode size={22} />
                QR 보기
              </button>
            </div>
          ) : (
            <div className="text-center space-y-3" data-testid="share-qr-panel">
              <div className="inline-block p-3 rounded-2xl bg-white shadow border border-black/5">
                {qrDataUrl ? (
                  <img src={qrDataUrl} alt="QR" width={200} height={200} />
                ) : (
                  <div className="w-[200px] h-[200px] flex items-center justify-center text-xs text-gray-400">QR 생성 중…</div>
                )}
              </div>
              <p className="text-[11px] text-gray-500">스캔하면 현재 소개중 상품으로 바로 이동합니다.</p>
              <div className="flex gap-2">
                <button onClick={copy} className="flex-1 py-2.5 rounded-full border border-black/10 dark:border-white/10 text-xs font-bold inline-flex items-center justify-center gap-1" data-testid="share-qr-copy"><Copy size={12} /> 링크 복사</button>
                <button onClick={() => setTab('menu')} className="flex-1 py-2.5 rounded-full bg-black text-white text-xs font-bold" data-testid="share-qr-back">← 돌아가기</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
